// NOTE: This file should normally not be modified unless you are adding a new provider.
// To add new routes, edit the AppRouter.tsx file.

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createHead, UnheadProvider } from '@unhead/react/client';
import { InferSeoMetaPlugin } from '@unhead/addons';
import { Suspense } from 'react';
import NostrProvider from '@/components/NostrProvider';
import { NostrSync } from '@/components/NostrSync';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { NostrLoginProvider } from '@nostrify/react/login';
import { AppProvider } from '@/components/AppProvider';
import { NWCProvider } from '@/contexts/NWCContext';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AppConfig } from '@/contexts/AppContext';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { BetaDisclaimerDialog } from '@/components/BetaDisclaimerDialog';
import { AmberCallbackHandler } from '@/components/auth/AmberCallbackHandler';
import AppRouter from './AppRouter';

const head = createHead({
  plugins: [
    InferSeoMetaPlugin(),
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60000, // 1 minute
      gcTime: Infinity,
    },
  },
});

const defaultConfig: AppConfig = {
  theme: "dark",
  relayMetadata: {
    relays: [
      { url: 'wss://auth.nostr1.com', read: true, write: true },
      { url: 'wss://wotr.relatr.xyz', read: true, write: true },
      { url: 'wss://relay.ditto.pub', read: true, write: true },
      { url: 'wss://goskatespots.nostr1.com', read: true, write: true },
      { url: 'wss://purplepag.es', read: true, write: false }, // Profile aggregator - read only
    ],
    updatedAt: 0,
  },
  useSkateRelay: true, // Use community relay by default for skate content
};

export function App() {
  return (
    <UnheadProvider head={head}>
      <LanguageProvider>
        <AppProvider storageKey="nostr:app-config" defaultConfig={defaultConfig}>
          <QueryClientProvider client={queryClient}>
            <NostrLoginProvider storageKey='nostr:login'>
              <NostrProvider>
                <NostrSync />
                <AmberCallbackHandler />
                <NWCProvider>
                  <TooltipProvider>
                    <Toaster />
                    <PWAInstallPrompt />
                    <BetaDisclaimerDialog />
                    <Suspense>
                      <AppRouter />
                    </Suspense>
                  </TooltipProvider>
                </NWCProvider>
              </NostrProvider>
            </NostrLoginProvider>
          </QueryClientProvider>
        </AppProvider>
      </LanguageProvider>
    </UnheadProvider>
  );
}

export default App;
